/* ============================================================
   entitlementContext.tsx — Global Pro-state provider.
   ------------------------------------------------------------
   Owns the single CustomerInfo subscription and pushes updates
   into a React Context. Every component that needs "is the user
   Pro?" reads from here via `useEntitlement()`. No component
   talks to the RevenueCat SDK directly for read access — that
   keeps RevenueCat as the unambiguous source of truth.

   Responsibilities:
     • subscribe to iap.ts's entitlement stream and mirror it
       into React state
     • re-identify the RevenueCat user whenever the Supabase
       session changes (login, logout, account switch)
     • refresh CustomerInfo when the app returns to the
       foreground (renewal/expiration may have happened while
       backgrounded)
     • cache available packages so the paywall renders instantly
       on second open
   ============================================================ */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  getAvailablePackages,
  getCachedEntitlement,
  identifyIapUser,
  logoutIapUser,
  refreshCustomerInfo,
  subscribeEntitlement,
  type EntitlementState,
  type IapPackage,
} from './iap';
import { iapLog } from './iapLogger';
import { useSession } from '../hooks/useSession';

interface EntitlementContextValue {
  /** Current entitlement state from RevenueCat (or its cache). */
  entitlement: EntitlementState;
  /** Convenience: same as entitlement.isPro. */
  isPro: boolean;
  /** True until the first CustomerInfo fetch resolves. */
  loading: boolean;
  /** Cached packages from the current offering. */
  packages: IapPackage[];
  /** True while packages are being fetched. */
  packagesLoading: boolean;
  /** Force a fresh CustomerInfo fetch. */
  refresh: () => Promise<void>;
  /** Force a fresh offering fetch (e.g. after dashboard change). */
  refreshPackages: () => Promise<void>;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

export function EntitlementProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const userId = session?.user?.id ?? null;

  const [entitlement, setEntitlement] = useState<EntitlementState>(
    () => getCachedEntitlement(),
  );
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<IapPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const lastIdentifiedRef = useRef<string | null>(null);

  // 1) Subscribe to the iap.ts stream — single source of truth.
  useEffect(() => {
    const unsub = subscribeEntitlement((s) => setEntitlement(s));
    return unsub;
  }, []);

  // 2) Identify/de-identify whenever the Supabase session changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (userId === lastIdentifiedRef.current) return;
      lastIdentifiedRef.current = userId;
      try {
        if (userId) {
          iapLog.info('session changed → identifying with RevenueCat', { userId });
          await identifyIapUser(userId);
        } else {
          iapLog.info('session cleared → logging RevenueCat user out');
          await logoutIapUser();
        }
      } catch (err) {
        iapLog.error('identify/logout failed', { err: String(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // 3) Refresh CustomerInfo when the app returns to the foreground.
  //    On iOS this catches the "user cancelled subscription in Settings,
  //    came back to the app" flow.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener: { remove: () => Promise<void> } | null = null;
    (async () => {
      try {
        const handle = await CapApp.addListener('appStateChange', async (state) => {
          if (state.isActive) {
            iapLog.debug('app became active — refreshing CustomerInfo');
            await refreshCustomerInfo();
          }
        });
        listener = handle as unknown as { remove: () => Promise<void> };
      } catch (err) {
        iapLog.warn('appStateChange listener could not attach', { err: String(err) });
      }
    })();
    return () => { listener?.remove?.().catch(() => {}); };
  }, []);

  // 4) Pre-warm the package cache so the paywall opens instantly.
  const loadPackages = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const pkgs = await getAvailablePackages();
      setPackages(pkgs);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  const refresh = useCallback(async () => {
    await refreshCustomerInfo();
  }, []);

  const value = useMemo<EntitlementContextValue>(
    () => ({
      entitlement,
      isPro: entitlement.isPro,
      loading,
      packages,
      packagesLoading,
      refresh,
      refreshPackages: loadPackages,
    }),
    [entitlement, loading, packages, packagesLoading, refresh, loadPackages],
  );

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

/** Consume the global entitlement state. Throws when used outside
 *  the provider — that's intentional: a missing provider is a bug
 *  we want to catch loudly, not paper over with a default. */
export function useEntitlement(): EntitlementContextValue {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error('useEntitlement() called outside <EntitlementProvider>');
  }
  return ctx;
}
